import {
  AccountBalanceOutlined,
  AddOutlined,
  BadgeOutlined,
  BusinessOutlined,
  CalendarTodayOutlined,
  DeleteOutlined,
  EditOutlined,
  EmailOutlined,
  FingerprintOutlined,
  HistoryOutlined,
  HomeOutlined,
  LocationOnOutlined,
  PersonOutlined,
  PhoneOutlined,
  ScheduleSend,
  UploadFileOutlined,
  VerifiedUserOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import { blue, green, grey, orange, red } from "@mui/material/colors";
import { isEmpty, isNil, size } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { formatPhone } from "../../../../_src_shared/utils/functions/formatPhone";
import { getDaysAgo } from "../../../../_src_shared/utils/functions/getDaysAgo";
import { maskSSN } from "../../../../_src_shared/utils/functions/maskSSN";
import JsonPreview from "../../../../components/common/JsonPreview";
import Flx from "../../../../components/layout/Flx";
import TabContent from "../../../../components/navigation/TabContent";
import TabSwitcher from "../../../../components/navigation/TabSwitcher";
import UploadFilesProcess from "../../../../components/processes/UploadFilesProcess";
import ResizeableSidebar from "../../../../components/sidebar/ResizeableSidebar";
import BasicFilesTable from "../../../../components/tables/BasicFilesTable";
import Txt from "../../../../components/typography/Txt";
import ChipItem from "../../../../components/ui/ChipItem";
import UserEmailAvatarRow from "../../../../components/ui/UserEmailAvatarRow";
import { useBorrowerPortalHook } from "../../../../hooks/useBorrowerPortalHook";
import { cachedDataItemSet } from "../../../../redux/actions/cachedDataActions";
import { CACHED_DATA_REDUCER_TYPES } from "../../../../redux/reducers/cachedDataReducer";
import { formatDateShort } from "../../../../utils/dates/formatDateShort";
import { BORROWER_SIDEBAR_FORM_TYPES } from "../../constants/BORROWER_SIDEBAR_FORM_TYPES";
import DetailField from "../shared/DetailField";
import DetailSection from "../shared/DetailSection";
import SectionDivider from "../shared/SectionDivider";
import BorrowerDetailsSidebarFormTypeRenderer from "./BorrowerDetailsSidebarFormTypeRenderer";
import EmptySectionDataContent from "./components/EmptySectionDataContent";
import ExperienceSummaryCard from "./components/ExperienceSummaryCard";
import QuickActionButton from "./components/QuickActionButton";
import BorrowerLoansTable from "./tables/BorrowerLoansTable";
import BorrowerValidatedExperienceTable from "./tables/BorrowerValidatedExperienceTable";
import { BORROWER_DETAILS_SIDEBAR_TAB_NAMES } from "../../constants/BORROWER_DETAILS_SIDEBAR_TAB_NAMES";

const CACHED_DATA_KEY = CACHED_DATA_REDUCER_TYPES.BORROWER_PORTAL_USERS;

const BorrowerDetailsSidebar = ({ onClose }) => {
  const borrowers = useSelector(
    (state) => state?.cachedData?.[CACHED_DATA_KEY],
  );
  const borrowersStaged = useSelector(
    (state) =>
      state?.cachedData?.[
        CACHED_DATA_REDUCER_TYPES.BORROWER_PORTAL_USERS_STAGED
      ],
  );
  const { getBorrowerProfile } = useBorrowerPortalHook();
  const loanPipeline = useSelector((state) => state.loanPipeline);
  const { id } = useParams();
  const dispatch = useDispatch();
  const borrower = useMemo(() => {
    const foundBorrower = borrowers?.find((u) => u?._id === id);
    if (isNil(foundBorrower)) {
      const foundStagedBorrower = borrowersStaged?.find((u) => u?._id === id);
      return foundStagedBorrower;
    }
    return foundBorrower;
  }, [borrowers, borrowersStaged, id]);

  const isStagedBorrower = useMemo(() => {
    const IS_STAGED = borrower?.staged_user;
    // console.log("borrower", { IS_STAGED, borrower });

    if (isNil(borrower)) {
      return true;
    }
    if (borrower?.isStaged) {
      return true;
    }
    return false;
  }, [borrower]);

  // const isStagedBorrower = useMemo(() => {
  //   return borrowersStaged?.some((u) => u?._id === id);
  // }, [borrowersStaged, id]);

  const filteredBorrowerLoans = useMemo(() => {
    return (
      loanPipeline?.filter((loan) => borrower?.dealIds?.includes(loan._id)) ||
      []
    );
  }, [borrower?.dealIds, loanPipeline]);

  const TABS = useMemo(() => {
    return [
      {
        label: `${BORROWER_DETAILS_SIDEBAR_TAB_NAMES.DETAILS}`,
        value: BORROWER_DETAILS_SIDEBAR_TAB_NAMES.DETAILS,
      },
      {
        label: `${BORROWER_DETAILS_SIDEBAR_TAB_NAMES.LOANS} (${size(filteredBorrowerLoans)})`,
        value: BORROWER_DETAILS_SIDEBAR_TAB_NAMES.LOANS,
      },
      {
        label: `${BORROWER_DETAILS_SIDEBAR_TAB_NAMES.DOCUMENTS} (${size(borrower?.borrowerDocuments)})`,
        value: BORROWER_DETAILS_SIDEBAR_TAB_NAMES.DOCUMENTS,
      },

      {
        label: `${BORROWER_DETAILS_SIDEBAR_TAB_NAMES.ENTITIES} (${size(borrower?.associatedEntities)})`,
        value: BORROWER_DETAILS_SIDEBAR_TAB_NAMES.ENTITIES,
      },
      {
        label: `${BORROWER_DETAILS_SIDEBAR_TAB_NAMES.EXPERIENCE} (${size(borrower?.validatedExperience)})`,
        value: BORROWER_DETAILS_SIDEBAR_TAB_NAMES.EXPERIENCE,
      },
      {
        label: `${BORROWER_DETAILS_SIDEBAR_TAB_NAMES.BANK_INFO} (${size(borrower?.bankAccounts)})`,
        value: BORROWER_DETAILS_SIDEBAR_TAB_NAMES.BANK_INFO,
      },
      {
        label: `${BORROWER_DETAILS_SIDEBAR_TAB_NAMES.RAW_JSON}`,
        value: BORROWER_DETAILS_SIDEBAR_TAB_NAMES.RAW_JSON,
      },
    ];
  }, [filteredBorrowerLoans, borrower]);

  const [tab, setTab] = useState(BORROWER_DETAILS_SIDEBAR_TAB_NAMES.DETAILS);
  const [formMeta, setFormMeta] = useState(null); // {type: "", data: {}}

  const updateBorrowersCachedData = (updatedBorrower) => {
    const originalBorrowers = [...borrowers];
    const updated = originalBorrowers.map((b) =>
      b._id === updatedBorrower._id ? updatedBorrower : b,
    );
    dispatch(cachedDataItemSet(CACHED_DATA_KEY, updated));
  };

  const description = useMemo(() => {
    return `${borrower?.emailAddress} | ${borrower?.phone ? formatPhone(borrower?.phone) : "No Phone"}`;
    // if (isNil(formMeta)) {
    //   return `${borrower?.emailAddress} | ${borrower?.phone ? formatPhone(borrower?.phone) : "No Phone"}`;
    // }
    // return formMeta.type;
  }, [borrower, formMeta]);

  const lastActiveColor = useMemo(() => {
    const count = getDaysAgo(borrower?.lastAccessed);
    if (count <= 31) {
      return green[50];
    }
    if (count > 31 && count < 60) {
      return orange[100];
    }
    if (count > 60) {
      return red[100];
    }
  }, [borrower?.lastAccessed]);

  const onSuccessFn = (d) => {
    updateBorrowersCachedData(d);
    setFormMeta(null);
  };

  if (isNil(borrower)) {
    return (
      <ResizeableSidebar
        open
        title={"Getting Borrower..."}
        hideBackdrop
        onClose={onClose}
        defaultWidth={780}
      >
        <Flx center sx={{ height: "100%", width: "100%" }}>
          <CircularProgress />
        </Flx>
      </ResizeableSidebar>
    );
  }

  return (
    <ResizeableSidebar
      open
      hideBackdrop
      onClose={onClose}
      description={description}
      title={`${borrower?.firstName} ${borrower?.lastName}`}
      bodyPy={isNil(formMeta) ? 3 : "0px"}
      bodyPx={isNil(formMeta) ? 2 : "0px"}
      headerSx={{ flexWrap: "wrap-reverse", alignItems: "flex-end", gap: 2 }}
      // bodySx={{ p: isNil(formMeta) ? 2 : 0 }}
      defaultWidth={780}
      headerEndContent={
        <Flx>
          {isNil(borrower?.lastAccessed) ? null : (
            <ChipItem
              icon={<HistoryOutlined />}
              // color="#424242"
              color="#232a31"
              sx={{ mr: 1 }}
              value={`Last active ${getDaysAgo(borrower?.lastAccessed) === 0 ? "Today" : `${getDaysAgo(borrower?.lastAccessed)} days ago`}`}
              backgroundColor={lastActiveColor}
            />
          )}
          {borrower?.isStaged && borrower?.stagedDate ? (
            <ChipItem
              icon={<ScheduleSend />}
              color="#232a31"
              sx={{ mr: 1 }}
              value={`Pending Invitation (Sent ${getDaysAgo(borrower?.stagedDate) === 0 ? "Today" : `${getDaysAgo(borrower?.stagedDate)} days ago`})`}
              backgroundColor={red[100]}
            />
          ) : null}
        </Flx>
      }
      headerBottomContent={
        isNil(formMeta) && !isStagedBorrower ? (
          <TabSwitcher
            fontSize="11px"
            tabs={TABS}
            value={tab}
            sx={{
              ml: 0.5,
              ".MuiButtonBase-root": {
                minWidth: "unset",
                fontWeight: 400,
                py: 1.5,
                minHeight: "unset",
                textTransform: "none",
              },
            }}
            onChange={setTab}
          />
        ) : (
          <TabSwitcher
            fontSize="11px"
            tabs={[
              {
                label: `${BORROWER_DETAILS_SIDEBAR_TAB_NAMES.DETAILS}`,
                value: BORROWER_DETAILS_SIDEBAR_TAB_NAMES.DETAILS,
              },

              {
                label: `${BORROWER_DETAILS_SIDEBAR_TAB_NAMES.RAW_JSON}`,
                value: BORROWER_DETAILS_SIDEBAR_TAB_NAMES.RAW_JSON,
              },
            ]}
            value={tab}
            sx={{
              ml: 0.5,
              ".MuiButtonBase-root": {
                minWidth: "unset",
                fontWeight: 400,
                py: 1.5,
                minHeight: "unset",
                textTransform: "none",
              },
            }}
            onChange={setTab}
          />
        )
      }
    >
      {isStagedBorrower ? (
        <Flx column>
          <TabContent
            activeTab={tab}
            tabKey={BORROWER_DETAILS_SIDEBAR_TAB_NAMES.DETAILS}
          >
            <DetailSection title="Staged Borrower Information (WIP)">
              <FlxGrid width="220px">
                <DetailField
                  label="Full Name"
                  value={
                    borrower?.fullName ||
                    `${borrower?.firstName || ""} ${borrower?.lastName || ""}`.trim()
                  }
                />
                <DetailField label="Email" value={borrower?.emailAddress} />
                <DetailField
                  label="Phone"
                  value={borrower?.phone ? formatPhone(borrower.phone) : null}
                />

                <DetailField
                  label="Staging Status"
                  value={borrower?.stagingStatus}
                />
                <DetailField
                  label="Staged By"
                  value={
                    borrower?.staging_user?.fullName ||
                    borrower?.staging_user?.emailAddress
                  }
                />
                <DetailField
                  label="Staged Date"
                  value={formatDate(borrower?.stagedDate)}
                />
                <DetailField
                  label="Staged Applications"
                  value={borrower?.stagedApplicationCount || 0}
                />
                <DetailField
                  label="Invite Sent"
                  value={
                    borrower?.invite_sent || borrower?.inviteSent ? "Yes" : "No"
                  }
                />
                <DetailField
                  label="Active Status"
                  value={borrower?.activeStatus}
                />
              </FlxGrid>
            </DetailSection>

            <SectionDivider />
            <DetailSection title="Account Actions">
              <DeleteBorrowerAccountButton
                emailAddress={borrower?.emailAddress}
                isStaged
                // isStaged={borrower?.staged_user}
              />
            </DetailSection>
          </TabContent>
          <TabContent
            activeTab={tab}
            tabKey={BORROWER_DETAILS_SIDEBAR_TAB_NAMES.RAW_JSON}
          >
            <JsonPreview sticky={false} defaultCollapse={2} values={borrower} />
          </TabContent>
        </Flx>
      ) : isNil(formMeta) ? (
        <Box>
          <TabContent
            activeTab={tab}
            tabKey={BORROWER_DETAILS_SIDEBAR_TAB_NAMES.DETAILS}
          >
            <BorrowerDetailsContent
              setFormMeta={setFormMeta}
              borrower={borrower}
              setTab={setTab}
            />
          </TabContent>
          <TabContent
            activeTab={tab}
            tabKey={BORROWER_DETAILS_SIDEBAR_TAB_NAMES.LOANS}
          >
            <BorrowerLoansTable rowData={filteredBorrowerLoans} />
          </TabContent>
          <TabContent
            activeTab={tab}
            tabKey={BORROWER_DETAILS_SIDEBAR_TAB_NAMES.DOCUMENTS}
          >
            <BorrowerFilesContent
              setFormMeta={setFormMeta}
              borrower={borrower}
              onSuccessFn={onSuccessFn}
            />
          </TabContent>

          <TabContent
            activeTab={tab}
            tabKey={BORROWER_DETAILS_SIDEBAR_TAB_NAMES.ENTITIES}
          >
            <BorrowerEntitiesContent
              setFormMeta={setFormMeta}
              borrower={borrower}
            />
          </TabContent>
          <TabContent
            activeTab={tab}
            tabKey={BORROWER_DETAILS_SIDEBAR_TAB_NAMES.EXPERIENCE}
          >
            <BorrowerExperienceContent
              setFormMeta={setFormMeta}
              borrower={borrower}
            />
          </TabContent>
          <TabContent
            activeTab={tab}
            tabKey={BORROWER_DETAILS_SIDEBAR_TAB_NAMES.BANK_INFO}
          >
            <BorrowerBankInfoContent
              setFormMeta={setFormMeta}
              borrower={borrower}
            />
          </TabContent>
          <TabContent
            activeTab={tab}
            tabKey={BORROWER_DETAILS_SIDEBAR_TAB_NAMES.RAW_JSON}
          >
            <JsonPreview sticky={false} defaultCollapse={2} values={borrower} />
          </TabContent>
        </Box>
      ) : (
        <BorrowerDetailsSidebarFormTypeRenderer
          type={formMeta?.type}
          data={formMeta?.data}
          borrowerId={borrower?._id}
          onSuccessFn={onSuccessFn}
          onClose={() => setFormMeta(null)}
        />
      )}
    </ResizeableSidebar>
  );
};

// ----------||||||||||-------- TAB CONTENTS ----------||||||||||--------
const BorrowerDetailsContent = ({ borrower, setFormMeta, setTab }) => {
  if (!borrower) return null;

  const addr = borrower?.primaryAddress;
  const idDoc = borrower?.borrowerIdDocument;

  return (
    <Box>
      {/* ── Personal Information ── */}
      <Flx wrap fw gap={4} sx={{ mb: 1.5 }}>
        <DetailSection
          title="Quick Actions"
          pt={1.5}
          sx={{ flexShrink: 1, flexGrow: 1, flexBasis: "400px" }}
        >
          {/* <FlxGrid width="110px"> */}
          <Flx wrap g={1}>
            <QuickActionButton
              onClick={() =>
                setFormMeta({
                  type: BORROWER_SIDEBAR_FORM_TYPES.UPDATE_PROFILE_DATA,
                  data: borrower,
                })
              }
              startIcon={<PersonOutlined className="thin" />}
            >
              Edit Profile
            </QuickActionButton>
            {/* <QuickActionButton
            onClick={() =>
              setFormMeta({
                type: BORROWER_SIDEBAR_FORM_TYPES.UPDATE_PERMITTED_USERS,
                data: borrower,
              })
            }
            startIcon={<PersonOutlined className="thin" />}
          >
            Edit Permitted Users
          </QuickActionButton> */}
            <QuickActionButton
              onClick={() =>
                setTab(BORROWER_DETAILS_SIDEBAR_TAB_NAMES.DOCUMENTS)
              }
              startIcon={<UploadFileOutlined className="thin" />}
            >
              Upload Documents
            </QuickActionButton>
            <QuickActionButton
              onClick={() =>
                setFormMeta({
                  type: BORROWER_SIDEBAR_FORM_TYPES.ADD_ENTITY,
                  data: null,
                })
              }
              startIcon={<BusinessOutlined className="thin" />}
            >
              Add Entity
            </QuickActionButton>
            <QuickActionButton
              onClick={() =>
                setFormMeta({
                  type: BORROWER_SIDEBAR_FORM_TYPES.ADD_EXPERIENCE,
                  data: null,
                })
              }
              startIcon={<HomeOutlined className="thin" />}
            >
              Add Experience
            </QuickActionButton>
            {/* <QuickActionButton startIcon={<EmailOutlined className="thin" />}>
            Invite to Portal
          </QuickActionButton> */}
            <QuickActionButton
              onClick={() =>
                setFormMeta({
                  type: BORROWER_SIDEBAR_FORM_TYPES.ADD_BANK_ACCOUNT,
                  data: null,
                })
              }
              startIcon={<AccountBalanceOutlined className="thin" />}
            >
              Add Bank Account
            </QuickActionButton>
            {/* <QuickActionButton
            startIcon={<ContentCopyOutlined className="thin" />}
          >
            Copy Borrower ID
          </QuickActionButton> */}
          </Flx>
        </DetailSection>
        <DetailSection
          headerMb={0.4}
          sx={{ flexBasis: "240px", flexShrink: 0, flexGrow: 1 }}
          title={
            size(borrower?.permittedUsers) >= 1
              ? `Permitted Users (${size(borrower?.permittedUsers)})`
              : "Permitted Users"
          }
        >
          {/* <SelectUserEmailInput /> */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              bgcolor: grey[50],
              p: 1.5,
              borderRadius: "8px",
              border: `1px solid ${grey[200]}`,
            }}
          >
            <UserEmailAvatarRow
              userEmails={borrower?.permittedUsers}
              includeName
              emptyLabel="No permitted users added for this borrower"
            />
            <Tooltip title="Edit Permitted Users">
              <IconButton
                onClick={() =>
                  setFormMeta({
                    type: BORROWER_SIDEBAR_FORM_TYPES.UPDATE_PERMITTED_USERS,
                    data: borrower,
                  })
                }
              >
                <EditOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        </DetailSection>
      </Flx>

      <SectionDivider />
      <DetailSection title="Borrower Information">
        <FlxGrid width="220px">
          <DetailField
            icon={<PersonOutlined sx={{ fontSize: 18 }} />}
            label="Full Name"
            value={
              borrower?.fullName ||
              `${borrower?.firstName || ""} ${borrower?.lastName || ""}`.trim()
            }
          />
          <DetailField
            icon={<EmailOutlined sx={{ fontSize: 18 }} />}
            label="Email"
            value={borrower?.emailAddress}
          />
          <DetailField
            icon={<PhoneOutlined sx={{ fontSize: 18 }} />}
            label="Phone"
            value={borrower?.phone ? formatPhone(borrower.phone) : null}
          />
          <DetailField
            icon={<CalendarTodayOutlined sx={{ fontSize: 18 }} />}
            label="Date of Birth"
            value={
              borrower?.dateOfBirth ? formatDate(borrower.dateOfBirth) : null
            }
          />
          <DetailField
            icon={<BadgeOutlined sx={{ fontSize: 18 }} />}
            label="SSN"
            value={maskSSN(borrower?.borrowerSSN)}
          />
          <DetailField
            icon={<VerifiedUserOutlined sx={{ fontSize: 18 }} />}
            label="Citizenship"
            value={borrower?.citizenshipStatus}
          />
          <DetailField label="Marital Status" value={borrower?.maritalStatus} />
          <DetailField
            label="Referral Source"
            value={borrower?.referralSource}
          />
        </FlxGrid>
      </DetailSection>

      <SectionDivider sx={{ mb: 3 }} />

      {/* ── Address ── */}
      <Flx fw wrap>
        <DetailSection sx={{ flexGrow: 1, py: 3 }} title="Primary Address">
          {addr?.fullAddress ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                bgcolor: grey[50],
                p: 1.5,
                borderRadius: "8px",
                border: `1px solid ${grey[200]}`,
              }}
            >
              <LocationOnOutlined
                sx={{ fontSize: 18, color: grey[400], mt: "2px" }}
              />
              <Flx column>
                <Txt
                  sx={{ fontSize: "13px", fontWeight: 600, color: grey[800] }}
                >
                  {addr.streetNumber} {addr.streetName}
                </Txt>
                <Flx ac g={1.5}>
                  <Txt sx={{ fontSize: "12px", color: grey[500], mt: 0.25 }}>
                    {addr.city}, {addr.state} {addr.zip}
                  </Txt>
                  {addr.county && (
                    <Txt sx={{ fontSize: "11px", color: grey[400], mt: 0.25 }}>
                      County: {addr.county}
                    </Txt>
                  )}
                </Flx>
              </Flx>
            </Box>
          ) : (
            <Txt
              sx={{ fontSize: "12px", color: grey[400], fontStyle: "italic" }}
            >
              No address on file
            </Txt>
          )}
        </DetailSection>
        <SectionDivider vertical sx={{ my: 0, mx: 3 }} />
        <DetailSection
          sx={{ flexGrow: 1, py: 3 }}
          title="Primary Identification"
        >
          {addr?.fullAddress ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                bgcolor: grey[50],
                p: 1.5,
                borderRadius: "8px",
                border: `1px solid ${grey[200]}`,
              }}
            >
              <FingerprintOutlined
                sx={{ fontSize: 18, color: grey[400], mt: "2px" }}
              />
              <Flx column>
                <Txt
                  sx={{ fontSize: "13px", fontWeight: 600, color: grey[800] }}
                >
                  {borrower?.borrowerIdDocument?.file_display_name}
                </Txt>
                <Flx ac g={1.5}>
                  <Txt sx={{ fontSize: "12px", color: grey[500], mt: 0.25 }}>
                    {borrower?.borrowerIdDocument?.uploadDate
                      ? `Uploaded ${formatDateShort(borrower?.borrowerIdDocument?.uploadDate)}`
                      : "Not Uploaded"}
                  </Txt>
                  {borrower?.borrowerIdDocument?.county && (
                    <Txt sx={{ fontSize: "11px", color: grey[400], mt: 0.25 }}>
                      County: {borrower?.borrowerIdDocument?.county}
                    </Txt>
                  )}
                </Flx>
              </Flx>
            </Box>
          ) : (
            <Txt
              sx={{ fontSize: "12px", color: grey[400], fontStyle: "italic" }}
            >
              No address on file
            </Txt>
          )}
        </DetailSection>
      </Flx>

      <SectionDivider sx={{ mt: 3 }} />

      {/* ── Account Details ── */}
      <DetailSection title="Account Details">
        <FlxGrid width="120px">
          <DetailField
            label="Sign Up Date"
            value={
              borrower?.signUpDate ? formatDate(borrower.signUpDate) : null
            }
          />
          <DetailField
            label="Last Accessed"
            value={
              borrower?.lastAccessed ? formatDate(borrower.lastAccessed) : null
            }
          />
          <DetailField
            label="Last Application"
            value={
              borrower?.lastApplicationDate
                ? formatDate(borrower.lastApplicationDate)
                : null
            }
          />
          <DetailField
            label="Last Upload"
            value={
              borrower?.lastUploadDate
                ? formatDate(borrower.lastUploadDate)
                : null
            }
          />
        </FlxGrid>

        <Flx ac gap={1.5} sx={{ mt: 1.5, flexWrap: "wrap" }}>
          <ChipItem
            value={
              borrower?.profileComplete
                ? "Profile Complete"
                : "Profile Incomplete"
            }
            backgroundColor={borrower?.profileComplete ? green[50] : orange[50]}
          />
          <ChipItem
            value={borrower?.borowerIdUploaded ? "ID Uploaded" : "ID Missing"}
            backgroundColor={borrower?.borowerIdUploaded ? green[50] : red[50]}
          />
          <ChipItem
            value={borrower?.smsOptIn ? "SMS Opted In" : "SMS Opted Out"}
            backgroundColor={borrower?.smsOptIn ? green[50] : grey[100]}
          />
        </Flx>
      </DetailSection>

      <SectionDivider />

      {/* ── Experience Summary ── */}
      <DetailSection title="Experience Summary">
        <FlxGrid width="240px" g={1.5}>
          <ExperienceSummaryCard
            label="Fix & Flip (Sold)"
            recent={borrower?.ff_sold?.less_than_three_years || 0}
            older={borrower?.ff_sold?.greater_than_three_years || 0}
          />
          <ExperienceSummaryCard
            label="Ground-Up (Sold)"
            recent={borrower?.guc_sold?.less_than_three_years || 0}
            older={borrower?.guc_sold?.greater_than_three_years || 0}
          />
          <ExperienceSummaryCard
            label="Income Producing"
            recent={
              borrower?.income_producing_properties?.less_than_three_years || 0
            }
            older={
              borrower?.income_producing_properties?.greater_than_three_years ||
              0
            }
          />
          <ExperienceSummaryCard
            label="Fix & Flip (Retained)"
            recent={borrower?.ff_retained_as_rental?.less_than_three_years || 0}
            older={
              borrower?.ff_retained_as_rental?.greater_than_three_years || 0
            }
          />
          <ExperienceSummaryCard
            label="General Contractor"
            recent={
              borrower?.general_contractor_projects?.less_than_three_years || 0
            }
            older={
              borrower?.general_contractor_projects?.greater_than_three_years ||
              0
            }
          />
        </FlxGrid>
      </DetailSection>
      <SectionDivider />
      <DetailSection title="Account Actions">
        <DeleteBorrowerAccountButton emailAddress={borrower?.emailAddress} />
      </DetailSection>
    </Box>
  );
};

const BorrowerEntitiesContent = ({ borrower, setFormMeta }) => {
  const associatedEntities = borrower?.associatedEntities;

  return (
    <DetailSection
      title="Associated Entities"
      count={size(associatedEntities)}
      endContent={
        <Button
          startIcon={<AddOutlined />}
          onClick={() =>
            setFormMeta({
              type: BORROWER_SIDEBAR_FORM_TYPES.ADD_ENTITY,
              initialValues: null,
            })
          }
        >
          Add Entity
        </Button>
      }
    >
      {isEmpty(associatedEntities) ? (
        <EmptySectionDataContent icon={<BusinessOutlined />}>
          No associated entities
        </EmptySectionDataContent>
      ) : (
        <Stack spacing={1.5}>
          {associatedEntities.map((entity, i) => (
            <Box
              key={entity?._id || i}
              sx={{
                border: `1px solid ${grey[200]}`,
                borderRadius: "8px",
                p: 2,
                bgcolor: "#fff",
              }}
            >
              <Flx jb ac sx={{ mb: 1 }}>
                <Txt
                  sx={{ fontSize: "14px", fontWeight: 600, color: grey[800] }}
                >
                  {entity.entityName}
                </Txt>
                <Flx ac g={1}>
                  <Chip
                    label={entity.entityType}
                    size="small"
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      height: 22,
                      bgcolor: blue[50],
                      color: blue[700],
                    }}
                  />
                  <Tooltip title="Edit Entity">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setFormMeta({
                          type: BORROWER_SIDEBAR_FORM_TYPES.UPDATE_ENTITY,
                          data: entity,
                        })
                      }
                    >
                      <EditOutlined sx={{ fontSize: "18px" }} />
                    </IconButton>
                  </Tooltip>
                </Flx>
              </Flx>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: "8px 16px",
                  pt: 1.5,
                  borderTop: `1px solid ${grey[200]}`,
                }}
              >
                <DetailField
                  label="State"
                  value={entity.entityStateOfOrganization || "—"}
                />
                <DetailField
                  label="Ownership"
                  value={
                    entity.applicantEntityPercentOwnership != null
                      ? `${(entity.applicantEntityPercentOwnership * 100).toFixed(0)}%`
                      : "—"
                  }
                />
                <DetailField
                  label="Organization Date"
                  value={
                    entity.entityOrganizationDate
                      ? formatDate(entity.entityOrganizationDate)
                      : "—"
                  }
                />
                <DetailField
                  label="EIN"
                  value={entity.entityEINNumber || "—"}
                />
                <DetailField
                  label="Entity Members"
                  value={size(entity.entityMembers) || 0}
                />
                <DetailField
                  label="Track Record"
                  value={size(entity.trackRecord) || 0}
                  entries
                />
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </DetailSection>
  );
};

const BorrowerBankInfoContent = ({ borrower, setFormMeta }) => {
  const bankAccounts = borrower?.bankAccounts;

  return (
    <DetailSection
      title="Bank Account Information"
      count={size(bankAccounts)}
      endContent={
        <Button
          startIcon={<AddOutlined />}
          onClick={() =>
            setFormMeta({
              type: BORROWER_SIDEBAR_FORM_TYPES.ADD_BANK_ACCOUNT,
              initialValues: null,
            })
          }
        >
          Add Account
        </Button>
      }
    >
      {isEmpty(bankAccounts) ? (
        <EmptySectionDataContent icon={<BusinessOutlined />}>
          No accounts entities
        </EmptySectionDataContent>
      ) : (
        <Stack spacing={1.5}>
          {bankAccounts.map((account, i) => (
            <Box
              key={account?._id || i}
              sx={{
                border: `1px solid ${grey[200]}`,
                borderRadius: "8px",
                p: 2,
                bgcolor: "#fff",
              }}
            >
              <Flx
                jb
                ac
                sx={{ mb: 1, borderBottom: `1px solid ${grey[200]}`, pb: 1 }}
              >
                <Txt
                  sx={{ fontSize: "14px", fontWeight: 600, color: grey[800] }}
                >
                  {account?.nameOfBank}
                </Txt>
                <Flx ac g={1}>
                  <Chip
                    label={account?.bankAccountOwnerType}
                    size="small"
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      height: 22,
                      bgcolor: blue[50],
                      color: blue[700],
                    }}
                  />
                  <Tooltip title="Edit Entity">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setFormMeta({
                          type: BORROWER_SIDEBAR_FORM_TYPES.UPDATE_BANK_ACCOUNT,
                          data: account,
                        })
                      }
                    >
                      <EditOutlined sx={{ fontSize: "18px" }} />
                    </IconButton>
                  </Tooltip>
                </Flx>
              </Flx>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: "8px 16px",
                  p: 1,
                  mt: 1.5,
                  px: 2,
                  background: grey[100],
                  borderRadius: "8px",
                }}
              >
                <DetailField
                  label="Bank Name"
                  value={account?.nameOfBank || "—"}
                />
                <DetailField
                  label="Routing Number"
                  value={account?.bankRoutingNumber || "—"}
                />
                <DetailField
                  label="Bank Phone Number"
                  value={
                    account?.bankPhoneNumber
                      ? formatPhone(account?.bankPhoneNumber)
                      : "—"
                  }
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: "8px 16px",
                  // pt: 1.5,
                  mt: 1.5,
                  // borderTop: `1px solid ${grey[200]}`,
                }}
              >
                {/* -- */}
                <DetailField
                  label="Account Owner Type"
                  value={account?.bankAccountOwnerType || "—"}
                />
                <DetailField
                  label="Account Number"
                  value={account?.bankAccountNumber || "—"}
                />
                <DetailField
                  label="Account Owner Name"
                  value={account?.bankAccountOwnerName || "—"}
                />

                <DetailField
                  label="Preferred Payment Method"
                  value={account?.preferredPaymentMethod || "—"}
                />

                <DetailField
                  label="Last Wire Date"
                  value={
                    account?.lastWireDate
                      ? formatDateShort(account?.lastWireDate)
                      : "—"
                  }
                />
                <DetailField
                  label="Last Wire Verification Date"
                  value={
                    account?.lastWireVerificationDate
                      ? formatDateShort(account?.lastWireVerificationDate)
                      : "—"
                  }
                />
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </DetailSection>
  );
};

const BorrowerFilesContent = ({ borrower, setFormMeta, onSuccessFn }) => {
  const { loading, uploadBorrowerProfileDocuments } = useBorrowerPortalHook();

  const dispatch = useDispatch();
  const handleOnSubmit = (f, { onSuccessFn } = {}) => {
    uploadBorrowerProfileDocuments({
      _id: borrower?._id,
      files: f,
      onSuccessFn,
    });
  };
  return (
    <>
      <Flx column g={1.5}>
        <UploadFilesProcess
          loading={loading}
          onSubmit={handleOnSubmit}
          onCompleteFn={onSuccessFn}
        />
        <BasicFilesTable
          routeType={"useBorrowerPortalHook"}
          files={borrower?.borrowerDocuments}
          showStatus
          onEditClick={(d) =>
            setFormMeta({
              type: BORROWER_SIDEBAR_FORM_TYPES.UPDATE_DOCUMENT_METADATA,
              data: d,
            })
          }
          borrowerId={borrower?._id}
          compactTable
          hideIfEmpty
        />
        {/* <SectionBox sx={{ gap: 1, p: 0 }}>
          <BasicFilesTable
            files={borrower?.borrowerDocuments}
            showStatus
            compactTable
            hideIfEmpty
          />
        </SectionBox> */}
      </Flx>
    </>
  );
};

const BorrowerExperienceContent = ({ borrower, setFormMeta }) => {
  const experience = borrower?.validatedExperience || [];

  return (
    <Box>
      {/* Summary cards */}
      <DetailSection title="Self-Reported Summary">
        <FlxGrid width="240px">
          <ExperienceSummaryCard
            label="Fix & Flip (Sold)"
            recent={borrower?.ff_sold?.less_than_three_years || 0}
            older={borrower?.ff_sold?.greater_than_three_years || 0}
          />
          <ExperienceSummaryCard
            label="Ground-Up (Sold)"
            recent={borrower?.guc_sold?.less_than_three_years || 0}
            older={borrower?.guc_sold?.greater_than_three_years || 0}
          />
          <ExperienceSummaryCard
            label="Income Producing"
            recent={
              borrower?.income_producing_properties?.less_than_three_years || 0
            }
            older={
              borrower?.income_producing_properties?.greater_than_three_years ||
              0
            }
          />
        </FlxGrid>
      </DetailSection>

      <SectionDivider />

      {/* Validated experience list */}
      <DetailSection
        title="Validated Experience"
        count={size(experience)}
        endContent={
          <Button
            startIcon={<AddOutlined />}
            onClick={() =>
              setFormMeta({
                type: BORROWER_SIDEBAR_FORM_TYPES.ADD_EXPERIENCE,
                initialValues: null,
              })
            }
          >
            Add Experience Record
          </Button>
        }
      >
        {isEmpty(experience) ? (
          <EmptySectionDataContent icon={<HomeOutlined />}>
            No validated experience records
          </EmptySectionDataContent>
        ) : (
          <BorrowerValidatedExperienceTable
            rowData={experience}
            onRowClicked={(data) =>
              setFormMeta({
                type: BORROWER_SIDEBAR_FORM_TYPES.UPDATE_EXPERIENCE,
                data: data,
              })
            }
          />
        )}
      </DetailSection>
    </Box>
  );
};

const DeleteBorrowerAccountButton = ({ emailAddress, isStaged = false }) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const {
    deleteBorrowerUserAccount,
    deleteStagedBorrowerUserAccount,
    loading,
  } = useBorrowerPortalHook();
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const buttonText = useMemo(() => {
    if (showConfirmation) {
      return "Confirm Delete";
    }
    if (isStaged) {
      return "Delete Staged Borrower";
    }
    return "Delete Borrower";
  }, [showConfirmation, isStaged]);

  const onClick = useCallback(
    (props) => {
      if (showConfirmation) {
        if (isStaged) {
          deleteStagedBorrowerUserAccount({
            emailAddress,
            onSuccessFn: () => {
              setShowConfirmation(false);
              dispatch(
                cachedDataItemSet(
                  CACHED_DATA_REDUCER_TYPES.BORROWER_PORTAL_USERS,
                  null,
                ),
              );
              dispatch(
                cachedDataItemSet(
                  CACHED_DATA_REDUCER_TYPES.BORROWER_PORTAL_USERS_STAGED,
                  null,
                ),
              );
              navigate("/users");
            },
          });
          return;
        }
        deleteBorrowerUserAccount({
          emailAddress,
          onSuccessFn: () => {
            setShowConfirmation(false);
            dispatch(
              cachedDataItemSet(
                CACHED_DATA_REDUCER_TYPES.BORROWER_PORTAL_USERS,
                null,
              ),
            );
            dispatch(
              cachedDataItemSet(
                CACHED_DATA_REDUCER_TYPES.BORROWER_PORTAL_USERS_STAGED,
                null,
              ),
            );
            navigate("/users");
          },
        });
        return;
      }

      setShowConfirmation(true);
    },
    [emailAddress, showConfirmation, isStaged],
  );

  if (showConfirmation) {
    return (
      <Alert
        severity="error"
        action={
          <Flx g={1} jc="flex-end">
            <Button
              color="inherit"
              size="small"
              onClick={() => setShowConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              color="error"
              size="small"
              onClick={onClick}
              disabled={loading}
              loading={loading}
            >
              Confirm Delete{" "}
            </Button>
          </Flx>
        }
      >
        Are you sure you want to delete this{" "}
        {isStaged ? "staged borrower" : "borrower"}?
      </Alert>
    );
  }
  return (
    <Flx fw>
      <Button
        startIcon={<DeleteOutlined />}
        size="medium"
        color="error"
        onClick={onClick}
      >
        {buttonText}
      </Button>
    </Flx>
  );
};

export default BorrowerDetailsSidebar;
